// Per-mode wiring for the game reducer, selectors, and screens.
// Classic entries point at the v2.4 data and engine untouched;
// Advanced Capacity entries point at the section-16 data and engine.
// Screens read this config instead of importing Classic data
// directly, so one component tree serves both modes.

import { PLANTS, PLANT_BY_ID } from '../data/plants.js';
import { DEMAND_BY_YEAR } from '../data/demand.js';
import { DISASTERS, DISASTER_BY_ID } from '../data/disasters.js';
import {
  ADVANCED_DISASTER_POOL,
  ADVANCED_DISASTER_BY_ID
} from '../data/advancedDisasters.js';
import {
  ADVANCED_PLANTS,
  ADVANCED_PLANT_BY_ID,
  ADVANCED_DEMAND_BY_YEAR,
  ADVANCED_EOS_DUPLICATE_MULTIPLIER,
  ADVANCED_EOS_FIRST_COPY_PAYS_FULL,
  ADVANCED_STARTING_PORTFOLIO,
  ADVANCED_SLOTS_NEW_PER_YEAR,
  ADVANCED_SLOTS_CUMULATIVE_BY_YEAR
} from '../data/advancedCapacity.js';
import { EOS_DUPLICATE_MULTIPLIER } from '../data/constants.js';
import {
  SLOTS_NEW_PER_YEAR,
  SLOTS_CUMULATIVE_BY_YEAR
} from '../data/slots.js';
import {
  GAME_MODE,
  DEFAULT_GAME_MODE,
  GAME_MODE_LABEL,
  GAME_VERSION_BY_MODE
} from '../data/modes.js';
import { resolveYear } from '../engine/year.js';
import { resolveAdvancedYear } from '../engine/advancedCapacity.js';
import {
  computeClassicFinalResult,
  computeAdvancedFinalResult
} from './finalResult.js';

// Advanced year results also carry the classic display names so
// shared screens (cost rows, analytics, report header) keep working:
// demand/totalOutput/shortfall/shortfallCost mean the energy layer.
function resolveAdvancedYearWithAliases(args) {
  const r = resolveAdvancedYear(args);
  return {
    ...r,
    demand: r.annualEnergyDemand,
    totalOutput: r.actualAnnualGeneration,
    shortfall: r.energyShortfall,
    shortfallCost: r.energyShortfallCost
  };
}

// What may be BUILT, as opposed to what may be looked up. The two
// diverged when the legacy fleet arrived: those plants are owned from
// the start and must resolve by id everywhere, but they are not on the
// menu. Guarding on the lookup would let a SELECT_PLANT action build
// more of them - and they cost no capital.
function buildableIdsOf(plants) {
  return new Set(plants.map((p) => p.id));
}

export const MODE_CONFIG = {
  [GAME_MODE.CLASSIC]: {
    mode: GAME_MODE.CLASSIC,
    label: GAME_MODE_LABEL[GAME_MODE.CLASSIC],
    gameVersion: GAME_VERSION_BY_MODE[GAME_MODE.CLASSIC],
    plants: PLANTS,
    plantsById: PLANT_BY_ID,
    buildablePlantIds: buildableIdsOf(PLANTS),
    disasterPool: DISASTERS,
    disasterById: DISASTER_BY_ID,
    supportsRetirement: false,
    eosDuplicateMultiplier: EOS_DUPLICATE_MULTIPLIER,
    // Classic is frozen on the rule as written: every copy of a
    // duplicated plant is discounted.
    eosFirstCopyPaysFull: false,
    startingPortfolio: [],
    slotsNewPerYear: SLOTS_NEW_PER_YEAR,
    slotsCumulativeByYear: SLOTS_CUMULATIVE_BY_YEAR,
    demandForYear: (year) => DEMAND_BY_YEAR[year] ?? 0,
    peakDemandForYear: () => null,
    resolveYear,
    computeFinalResult: computeClassicFinalResult
  },
  [GAME_MODE.ADVANCED_CAPACITY]: {
    mode: GAME_MODE.ADVANCED_CAPACITY,
    label: GAME_MODE_LABEL[GAME_MODE.ADVANCED_CAPACITY],
    gameVersion: GAME_VERSION_BY_MODE[GAME_MODE.ADVANCED_CAPACITY],
    plants: ADVANCED_PLANTS,
    plantsById: ADVANCED_PLANT_BY_ID,
    buildablePlantIds: buildableIdsOf(ADVANCED_PLANTS),
    disasterPool: ADVANCED_DISASTER_POOL,
    disasterById: ADVANCED_DISASTER_BY_ID,
    supportsRetirement: true,
    eosDuplicateMultiplier: ADVANCED_EOS_DUPLICATE_MULTIPLIER,
    eosFirstCopyPaysFull: ADVANCED_EOS_FIRST_COPY_PAYS_FULL,
    // Section 16.15: the inherited fossil fleet, and the Advanced
    // slot table that pays for the two slots it occupies. Screens and
    // the reducer read these from here rather than importing
    // src/data/slots.js, which is shared with Classic.
    startingPortfolio: ADVANCED_STARTING_PORTFOLIO,
    slotsNewPerYear: ADVANCED_SLOTS_NEW_PER_YEAR,
    slotsCumulativeByYear: ADVANCED_SLOTS_CUMULATIVE_BY_YEAR,
    demandForYear: (year) => ADVANCED_DEMAND_BY_YEAR[year]?.energy ?? 0,
    peakDemandForYear: (year) => ADVANCED_DEMAND_BY_YEAR[year]?.peak ?? 0,
    resolveYear: resolveAdvancedYearWithAliases,
    computeFinalResult: computeAdvancedFinalResult
  }
};

export function configForMode(mode) {
  return MODE_CONFIG[mode] ?? MODE_CONFIG[DEFAULT_GAME_MODE];
}

export function configForState(state) {
  return configForMode(state?.mode);
}

export function isAdvancedMode(state) {
  return state?.mode === GAME_MODE.ADVANCED_CAPACITY;
}
